/**
 * Local persistence.
 *
 * Deliberately localStorage and nothing else: no account, no server, no
 * personal data leaving the phone. Somebody opens the link, grows
 * something, closes the tab, and finds it again next time.
 *
 * That constraint is load-bearing now that the register stores a name and a
 * place. Both are typed by the player, both stay on the device, and neither
 * is ever transmitted. If a hosted leaderboard is ever added, that becomes a
 * consent decision with a real privacy notice — not a silent upgrade to this
 * file.
 */

import type { AnyGardenSave } from '../game/Garden';
import { makeStamp, type VisitorStamp } from '../game/progress';
import { parseWindow, sanitiseCounters, type ChallengeWindow } from '../progression';
import type { SpeciesId } from '../content/species';

const KEY = 'world-garden.v1';

/**
 * What this key was called while the product carried a venue's name.
 *
 * Read once, moved across, and deleted — see `readRaw`. Keep this constant
 * for as long as any device might still be holding a garden under it: the
 * cost of the line is nothing, and the cost of dropping it is somebody's
 * garden.
 */
const LEGACY_KEY = 'ksp-ar-garden.v1';

export const PROFILE_VERSION = 2;

export interface SavedProfile {
  version: 2;
  /** Either format — `Garden.restore` migrates a v1 garden on the way in. */
  garden: AnyGardenSave;
  /** Ids of fact cards already revealed, so none is ever shown twice. */
  factsSeen: string[];
  muted: boolean;
  /**
   * Epoch ms this profile was last written. Growth is a function of the wall
   * clock, so this is the only thing needed to work out what happened while
   * the player was away.
   */
  lastSeenAt: number;
  /**
   * ISO `YYYY-MM-DD` days this device has been played on, newest last.
   *
   * The streak belongs to the device rather than to a gardener: it is what
   * makes coming back tomorrow visible, and on a shared phone there is no
   * meaningful per-person answer anyway.
   */
  days: string[];
  /** Badge ids earned. Sticky — a badge once earned is never taken back. */
  badges: string[];
  /**
   * Mutation ids ever brought to maturity, and weather ids ever witnessed.
   *
   * Both are sticky and both are needed because a mutation is derived from
   * the timeline, not stored on the plant — harvest or replace that specimen
   * and the fact that you once grew one has to survive somewhere.
   */
  mutationsSeen: string[];
  weatherSeen: string[];
  /** The roll of gardeners who have planted on this device. */
  register: VisitorStamp[];
  /** Whose stamp is currently being updated, if anyone has signed. */
  activeStampId: string | null;
  /** Sticky: a tree has matured during an absence at least once. */
  maturedWhileAway: boolean;
  /** Local hour of the most recent tend, for the Night Gardener badge. */
  lastTendHour: number | null;
  /** Seedlings banked by harvesting. Belong to the device, like the streak. */
  seedlings: number;
  /**
   * Every species ever brought to maturity on this device.
   *
   * Sticky, and necessary for the same reason `mutationsSeen` is: harvest a
   * specimen and the garden no longer knows you grew it. Without this, lifting
   * your only sidr would retract the Botanist badge you had already earned.
   */
  speciesGrown: SpeciesId[];
  /** Specimens lifted on this device, ever. Drives the harvest badges. */
  harvested: number;
  /**
   * Specimens brought to maturity on this device, ever.
   *
   * Distinct from what is standing and from `harvested`: this is the number
   * the garden's tier is derived from, and a district once opened must never
   * close again. Counting standing trees would shut the wetland the moment
   * somebody harvested one.
   */
  treesGrown: number;
  /**
   * Lifetime counters for the progression engine (`src/progression/`).
   *
   * Only the metrics that have no other home live here — see
   * `STORED_METRICS`. XP and level are never stored; they are derived from
   * these and the records above on every read.
   */
  counters: Record<string, number>;
  /** The open daily and weekly challenge windows, if any have opened. */
  challenges: { daily: ChallengeWindow | null; weekly: ChallengeWindow | null };
  /**
   * The highest level whose reward has been paid.
   *
   * The one piece of level state that *is* stored, because a reward is an
   * event: re-tuning the curve can move a player's level, but must never pay
   * the same level twice.
   */
  levelClaimed: number;
}

/** What v1 wrote. Read only long enough to migrate it. */
interface LegacyProfile {
  garden: AnyGardenSave;
  factsSeen?: string[];
  muted?: boolean;
}

export function emptyProfile(now: number): SavedProfile {
  return {
    version: PROFILE_VERSION,
    garden: { version: 2, plants: [] },
    factsSeen: [],
    muted: false,
    lastSeenAt: now,
    days: [],
    badges: [],
    mutationsSeen: [],
    weatherSeen: [],
    register: [],
    activeStampId: null,
    maturedWhileAway: false,
    lastTendHour: null,
    seedlings: 0,
    speciesGrown: [],
    harvested: 0,
    treesGrown: 0,
    counters: {},
    challenges: { daily: null, weekly: null },
    levelClaimed: 1,
  };
}

/**
 * Fill in fields a stamp written by an earlier build does not have.
 *
 * The spread in `loadProfile` only reaches the profile's own keys — it cannot
 * see inside the register array — so entries have to be normalised here or
 * every read site needs its own `?? 0`.
 */
function normaliseStamp(stamp: VisitorStamp): VisitorStamp {
  return {
    ...makeStamp(stamp.name ?? '', stamp.from ?? '', stamp.stampedAt || Date.now()),
    ...stamp,
    harvested: stamp.harvested ?? 0,
    harvestScore: stamp.harvestScore ?? 0,
    harvestSpecies: stamp.harvestSpecies ?? [],
  };
}

/**
 * The stored profile, moving it off the old key the first time it is found
 * there.
 *
 * The rename is a one-time read, not a dual read for ever: once the value
 * sits under the new key the old entry is deleted, so there is never a
 * moment where two copies of a garden exist and the app has to guess which
 * is current. The new key wins if both somehow exist — it is the one every
 * write since the rename has gone to.
 *
 * A failed write is survivable: the value is still returned, and the next
 * load tries the move again.
 */
function readRaw(): string | null {
  const current = localStorage.getItem(KEY);
  if (current) return current;

  const legacy = localStorage.getItem(LEGACY_KEY);
  if (!legacy) return null;
  try {
    localStorage.setItem(KEY, legacy);
    localStorage.removeItem(LEGACY_KEY);
  } catch {
    /* Quota or private browsing. The garden is still readable this session. */
  }
  return legacy;
}

export function loadProfile(): SavedProfile | null {
  try {
    const raw = readRaw();
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SavedProfile | LegacyProfile;
    if (!parsed || typeof parsed !== 'object' || !('garden' in parsed)) return null;

    if ((parsed as SavedProfile).version === PROFILE_VERSION) {
      // Defensive: a save written by an earlier build of v2 may be missing
      // fields added since, and every call site here iterates or reads them.
      const p = parsed as Partial<SavedProfile>;
      return {
        ...emptyProfile(Date.now()),
        ...p,
        version: PROFILE_VERSION,
        factsSeen: p.factsSeen ?? [],
        days: p.days ?? [],
        badges: p.badges ?? [],
        mutationsSeen: p.mutationsSeen ?? [],
        weatherSeen: p.weatherSeen ?? [],
        register: (p.register ?? []).map(normaliseStamp),
        speciesGrown: p.speciesGrown ?? [],
        seedlings: p.seedlings ?? 0,
        harvested: p.harvested ?? 0,
        treesGrown: p.treesGrown ?? 0,
        counters: sanitiseCounters(p.counters),
        challenges: {
          daily: parseWindow(p.challenges?.daily),
          weekly: parseWindow(p.challenges?.weekly),
        },
        // A save from before levels existed has claimed nothing. Level 1 is
        // where everybody starts and pays nothing, so that is the floor.
        levelClaimed:
          typeof p.levelClaimed === 'number' && p.levelClaimed >= 1
            ? Math.floor(p.levelClaimed)
            : 1,
        lastSeenAt: p.lastSeenAt || Date.now(),
      };
    }

    return migrateLegacy(parsed as LegacyProfile);
  } catch {
    // Private browsing, quota, or a corrupt entry. Losing a save is
    // survivable; crashing on boot is not.
    return null;
  }
}

/**
 * A v1 profile had no clock at all — plants were pure tap counts.
 *
 * `lastSeenAt` is set to *now* rather than guessed, so migration never hands
 * anyone phantom offline growth for time they did not actually wait. The
 * garden resumes at exactly the size they left it.
 */
function migrateLegacy(legacy: LegacyProfile): SavedProfile | null {
  const now = Date.now();
  const garden = legacy.garden;
  if (!garden || typeof garden.version !== 'number') return null;

  const profile = emptyProfile(now);
  profile.factsSeen = legacy.factsSeen ?? [];
  profile.muted = legacy.muted ?? false;
  // `Garden.restore` accepts either format and does the real conversion; it
  // is the only place that knows each species' grow time.
  profile.garden = garden;
  return profile;
}

export function saveProfile(profile: SavedProfile): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(profile));
  } catch {
    /* Storage unavailable — the session simply won't persist. */
  }
}

export function clearProfile(): void {
  try {
    localStorage.removeItem(KEY);
    // Both, or a clear would be undone by the migration on the next load.
    localStorage.removeItem(LEGACY_KEY);
  } catch {
    /* no-op */
  }
}

/** Debounce writes so rapid tapping doesn't hammer localStorage. */
export function makeDebouncedSaver(delayMs = 700): (p: SavedProfile) => void {
  let timer: number | null = null;
  let pending: SavedProfile | null = null;
  return (profile) => {
    pending = profile;
    if (timer !== null) return;
    timer = window.setTimeout(() => {
      timer = null;
      if (pending) saveProfile(pending);
      pending = null;
    }, delayMs);
  };
}
